import { getCloudinaryId } from '@masterplandev/utils'

import { env } from '@/env'

import { CloudImageOptions, GetCloudImageUrl } from '../types'
import { removeImageExtensions } from '../utils'

interface CloudinaryConfig {
  cloudName: string
  cname?: string
  secureDistribution?: string
  privateCdn?: boolean
  secure?: boolean
}

const CLOUDINARY_CONFIG: CloudinaryConfig = {
  cloudName: env.REACT_APP_CLOUDINARY_CLOUD_NAME || '',
  cname: env.REACT_APP_CLOUDINARY_CNAME || '',
  secureDistribution: env.REACT_APP_CLOUDINARY_CNAME || '',
  privateCdn: env.REACT_APP_CLOUDINARY_PRIVATE_CDN,
  secure: true,
}

/**
 * Create the URL prefix for Cloudinary resources.
 * Available use cases
 * http://res.cloudinary.com/{cloudName}
 * https://res.cloudinary.com/{cloudName}
 * https://{cloudName}-res.cloudinary.com/
 * http://{domain}/${cloudName}
 * https://{domain}/${cloudName}
 * https://{domain}
 */
function getUrlPrefix(config: CloudinaryConfig) {
  const {
    cloudName,
    secure,
    privateCdn: privateCDN,
    cname,
    secureDistribution,
  } = config

  if (!secure && !cname) {
    return `http://res.cloudinary.com/${cloudName}`
  }

  if (secure && !secureDistribution && privateCDN) {
    return `https://${cloudName}-res.cloudinary.com`
  }

  if (secure && !secureDistribution) {
    return `https://res.cloudinary.com/${cloudName}`
  }

  if (secure && secureDistribution && privateCDN) {
    return `https://${secureDistribution}`
  }

  if (secure && secureDistribution) {
    return `https://${secureDistribution}/${cloudName}`
  }

  if (!secure && cname) {
    return `http://${cname}/${cloudName}`
  }

  throw new Error(
    'Invalid Cloudinary configuration: unable to determine URL prefix',
  )
}

export function getCloudinaryUrl() {
  const urlPrefix = getUrlPrefix(CLOUDINARY_CONFIG)
  const assetType = 'image'
  const deliveryType = 'upload'

  return `${urlPrefix}/${assetType}/${deliveryType}`
}

export function getCloudinaryImageId(id: string) {
  return removeImageExtensions(getCloudinaryId(id) ?? id)
}

function toCloudinaryBackgroundColor(color: string): string | undefined {
  if (color === 'transparent') {
    return color
  }

  const hexColor = color.match(/^#([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i)

  if (hexColor) {
    return `rgb:${hexColor[1]}`
  }

  const rgbColor = color.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i,
  )

  if (!rgbColor) {
    return undefined
  }

  const channels = rgbColor.slice(1, 4).map(Number)

  if (channels.some((channel) => channel > 255)) {
    return undefined
  }

  if (rgbColor[4] !== undefined) {
    channels.push(Number(rgbColor[4]) * 255)
  }

  return `rgb:${channels
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`
}

/**
 * Converts image options object to Cloudinary transformation string
 * Documentation: {@link https://cloudinary.com/documentation/image_transformations#transformation_url_syntax|Cloudinary Image Transformations}
 *
 * @param options - The options for the image
 * @returns The Cloudinary transformation string
 */
export function getTransformations(options: CloudImageOptions): string {
  // Build transformation groups according to Cloudinary URL format
  const transformations: string[] = []

  // Group 1: Crop/resize parameters (c, g, h, w)
  const cropParams: string[] = []

  // Map fit to Cloudinary crop mode (c_)
  if (options.fit) {
    const fitMap: Record<NonNullable<CloudImageOptions['fit']>, string> = {
      cover: 'fill',
      contain: 'fit',
      crop: 'crop',
      'scale-down': 'limit',
      pad: 'pad',
    }
    cropParams.push(`c_${fitMap[options.fit]}`)
  }

  if (options.background) {
    const background = toCloudinaryBackgroundColor(options.background)

    if (background) {
      cropParams.push(`b_${background}`)
    }
  }

  // Map gravity (g_)
  if (options.gravity) {
    const gravityMap: Record<
      NonNullable<CloudImageOptions['gravity']>,
      string
    > = {
      auto: 'auto',
      face: 'face',
      top: 'north',
      bottom: 'south',
      left: 'west',
      right: 'east',
    }
    cropParams.push(`g_${gravityMap[options.gravity]}`)
  }

  if (options.height) {
    cropParams.push(`h_${options.height}`)
  }

  if (options.width) {
    cropParams.push(`w_${options.width}`)
  }

  if (cropParams.length > 0) {
    transformations.push(cropParams.join(','))
  }

  // Group 2: Format (f_)
  if (options.format) {
    transformations.push(`f_${options.format}`)
  }

  // Group 3: Quality (q_)
  if (options.quality) {
    const qualityMap: Record<
      NonNullable<CloudImageOptions['quality']>,
      string
    > = {
      high: 'best',
      'medium-high': 'good',
      'medium-low': 'eco',
      low: 'low',
    }
    transformations.push(`q_auto:${qualityMap[options.quality]}`)
  }

  // Group 4: DPR (dpr_)
  if (options.dpr) {
    transformations.push(`dpr_${Number(options.dpr).toFixed(1)}`)
  }

  return transformations.join('/')
}

export const getCloudinaryImageUrl: GetCloudImageUrl = (id, options) => {
  const cloudinaryUrl = getCloudinaryUrl()
  const version = 'v1'
  const cloudinaryId = getCloudinaryImageId(id)
  const transformations = getTransformations({
    format: 'auto',
    ...options,
  })

  return `${cloudinaryUrl}/${transformations}/${version}/${cloudinaryId}`
}
